import React, { createContext, FC, PropsWithChildren, useEffect, useMemo, useState } from "react";

import { Navigate } from "react-router-dom";

import { Permissions, RouteItem } from "@/shared/domain";
import { AppRoute } from "@/shared/domain/navigation";
import { isABroker, useAuth } from "@/shared/hooks";

import {
    claimsRoutes,
    contractsRoutes,
    conversationsRoutes,
    damageRoutes,
    insuredGoodsRoutes,
    invoicesRoutes,
    linkedDocumentsRoutes,
    myAccountRoutes,
    otherRoutes,
    tarifficationRoutes,
    toDoRoutes,
} from "../../routes/index";

export const RouteCollectionContext = createContext({ routeCollection: [] as RouteItem[] });
RouteCollectionContext.displayName = "RouteCollectionContext";

export const RouteCollectionProvider: FC<PropsWithChildren> = ({ children }) => {
    const {
        user,
        scopeOptions: { onlyAllowedRoutes },
    } = useAuth();
    const [routeCollection, setRouteCollection] = useState<RouteItem[]>([]);

    const refreshRouteCollection = () => {
        let newRouteCollection = [
            ...(user.hasGlobalPermission(Permissions.ViewLinkedDocuments) ? linkedDocumentsRoutes() : []),
            ...invoicesRoutes(user),
            ...(user.hasGlobalPermission(Permissions.ViewRisks) ? insuredGoodsRoutes(user) : []),
            ...(!isABroker() && user.hasGlobalPermission(Permissions.AccessConversations) ? conversationsRoutes() : []),
            ...(!isABroker() ? damageRoutes() : []),
            ...(!isABroker() ? toDoRoutes(user) : []),
            ...(!isABroker() ? tarifficationRoutes(user) : []),
            ...myAccountRoutes(user),
            ...contractsRoutes(user),
            ...claimsRoutes(user),
            ...otherRoutes(),
        ].filter(Boolean);
        const { scope } = user;
        let mainRoute: string = AppRoute.Root;

        if (scope) {
            if (onlyAllowedRoutes) {
                newRouteCollection = newRouteCollection.filter(
                    (route) => route.allScopesAllowed || route?.allowScopes?.includes?.(scope),
                );
            }

            newRouteCollection = newRouteCollection.filter((route) => !route?.denyScopes?.includes?.(scope));
            // If the root path is not inside the valid routes, use the first one valid to redirect
            if (!newRouteCollection.some((route) => route.path === AppRoute.Root)) {
                mainRoute = newRouteCollection[0].path;
            }
        }

        newRouteCollection.push({
            path: "*",
            element: <Navigate to={mainRoute} replace />,
        });

        setRouteCollection(newRouteCollection);
    };

    useEffect(() => {
        refreshRouteCollection();
    }, [user]);

    const contextValue = useMemo(
        () => ({
            routeCollection,
        }),
        [routeCollection, user],
    );

    return <RouteCollectionContext.Provider value={contextValue}>{children}</RouteCollectionContext.Provider>;
};
